import { Dropdown } from 'react-bootstrap';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import type { ServiceType } from '@econtractor/types';
import * as apiService from 'api/apiServices';
import { useToast } from 'context/ToastContext';
import SvgArchive from 'components/svg/SvgArchive';
import SvgCheck from 'components/svg/SvgCheck';
import SvgClipCopy from 'components/svg/svgClipCopy';
import SvgEdit from 'components/svg/SvgEdit';
import SvgTrash from 'components/svg/SvgTrash';
import { useService } from './context/ServiceProvider';
import useDeleteService from './hooks/hookDeleteService';

interface ServiceTableRowActionsProps {
    service: ServiceType;
    fetchServices: (force?: boolean) => Promise<unknown>;
}

const ServiceTableRowActions = ({ service, fetchServices }: ServiceTableRowActionsProps) => {
    const { showToast } = useToast();
    const { t } = useTranslation(['Services', 'common']);
    const { setShowEditService, setShowSpinner } = useService();
    const { deleteService, archiveService } = useDeleteService();

    const cbDeleteService = async () => {
        setShowSpinner(true);
        const resActive = await apiService.deleteService(service.ID);
        if (!resActive) showToast(t('common:messError'), 'danger');
        await fetchServices(true);
        setShowSpinner(false);
    };

    const cbArchiveService = async () => {
        setShowSpinner(true);
        const resActive = await apiService.setStat([service.ID], 0);
        if (!resActive) showToast(t('common:messError'), 'danger');
        await fetchServices(true);
        setShowSpinner(false);
    };

    const handleClickActive = async () => {
        setShowSpinner(true);
        const resActive = await apiService.setStat([service.ID], 1);
        if (!resActive) showToast(t('common:messError'), 'danger');
        await fetchServices(true);
        setShowSpinner(false);
    };

    const renderMenuItem = (
        icon: React.ReactNode,
        title: string,
        subtitle: string,
        onSelect: () => void,
        options?: { danger?: boolean },
    ) => (
        <Dropdown.Item
            as="button"
            className={options?.danger ? 'text-danger' : undefined}
            onClick={(e) => {
                onSelect();
                e.currentTarget.blur();
            }}
        >
            <div className="d-flex align-items-start w-100 text-start">
                <span
                    className={`d-inline-flex align-items-center justify-content-center flex-shrink-0 me-2 item-icon${
                        options?.danger ? ' text-danger' : ''
                    }`}
                >
                    {icon}
                </span>
                <div className="flex-grow-1 min-w-0">
                    <div className={`fw-medium ${options?.danger ? 'text-danger' : ''}`}>{title}</div>
                    <small className="text-muted warehouse-team-menu-hint">{subtitle}</small>
                </div>
            </div>
        </Dropdown.Item>
    );

    const menu = (
        <Dropdown.Menu
            className="p-2 shadow-sm warehouse-team-menu"
            popperConfig={{ strategy: 'fixed' }}
            renderOnMount
        >
            {renderMenuItem(
                <SvgEdit />,
                t('servPopup.edit'),
                t('servPopup.editHint'),
                () => setShowEditService(service.ID),
            )}

            {service.servStatus === 1
                ? renderMenuItem(
                      <SvgArchive />,
                      t('servPopup.archive'),
                      t('servPopup.archiveHint'),
                      () => archiveService(cbArchiveService),
                  )
                : renderMenuItem(
                      <SvgCheck />,
                      t('servPopup.active'),
                      t('servPopup.activeHint'),
                      () => {
                          void handleClickActive();
                      },
                  )}

            <Dropdown.Divider className="my-2" />

            {renderMenuItem(
                <SvgClipCopy />,
                t('servPopup.clone'),
                t('servPopup.cloneHint'),
                () => setShowEditService(-service.ID),
            )}

            <Dropdown.Divider className="my-2" />

            {renderMenuItem(
                <SvgTrash />,
                t('servPopup.delete'),
                t('servPopup.deleteHint'),
                () => deleteService(cbDeleteService),
                { danger: true },
            )}
        </Dropdown.Menu>
    );

    return (
        <Dropdown align="end">
            <Dropdown.Toggle
                className="p-1 py-0"
                size="sm"
                variant="light-secondary"
                id={`dropdown-service-${service.ID}`}
            >
                <i className="bi bi-three-dots"></i>
            </Dropdown.Toggle>

            {typeof document !== 'undefined' ? createPortal(menu, document.body) : menu}
        </Dropdown>
    );
};

export default ServiceTableRowActions;
